import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Save, Eye, RotateCcw, CloudOff, Cloud } from "lucide-react";
import { DOC_LIST, fetchDoc, saveDoc, getDefault } from "../../lib/legalContent";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

export default function AdminContent() {
  const [slug, setSlug] = useState(DOC_LIST[0].slug);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("default");

  const load = (s) => { setLoading(true); fetchDoc(s).then((d) => { setDoc({ title: d.title, body: d.body }); setSource(d.source); }).finally(() => setLoading(false)); };
  useEffect(() => { load(slug); }, [slug]);

  const save = async () => {
    setSaving(true);
    const where = await saveDoc(slug, doc);
    setSaving(false);
    if (where === "backend") { toast.success("Saved to backend — synced with the app."); setSource("backend"); }
    else { toast.success("Saved as a local draft on this site."); setSource("local"); }
  };
  const reset = () => { const d = getDefault(slug); setDoc({ title: d.title, body: d.body }); toast.message("Reverted to default text (not yet saved)."); };

  return (
    <div data-testid="admin-content">
      <div className="flex items-center gap-2"><FileText className="h-6 w-6 text-[var(--neon-cyan)]" /><h2 className="font-display uppercase text-2xl text-white">Content &amp; Legal</h2></div>
      <p className="mt-1 text-sm text-[var(--text-muted)]">Edit the Terms, Privacy, Rules and help pages shown across the site.</p>

      <div className="mt-3 rounded-lg border border-[rgba(0,229,255,.3)] bg-[rgba(0,229,255,.05)] px-4 py-3 text-sm text-[var(--neon-cyan)] flex items-start gap-2">
        {source === "backend" ? <Cloud className="h-4 w-4 mt-0.5" /> : <CloudOff className="h-4 w-4 mt-0.5" />}
        <span>{source === "backend"
          ? "Connected to the backend content API — edits sync with the mobile app."
          : "Backend content API not detected. Edits are saved as a local draft and shown on this site. Deploy the content backend patch (BACKEND_PATCH_content_legal.md) to sync these documents with the mobile app."}</span>
      </div>

      <div className="mt-4 grid lg:grid-cols-[240px_1fr] gap-5">
        <div className="flex lg:flex-col gap-1 overflow-x-auto rounded-xl border border-white/10 bg-[var(--surface)] p-2">
          {DOC_LIST.map((d) => (
            <button key={d.slug} onClick={() => setSlug(d.slug)} data-testid={`content-doc-${d.slug}`}
              className={`text-left whitespace-nowrap rounded-lg px-3 py-2 text-sm ${slug === d.slug ? "bg-[rgba(255,45,149,.12)] text-[var(--neon-pink)]" : "text-[var(--text-muted)] hover:bg-white/5 hover:text-white"}`}>{d.title}</button>
          ))}
        </div>

        <NeonCard variant="flow" innerClassName="p-5">
          {loading || !doc ? <Spinner /> : (
            <>
              <div className="flex items-center justify-between">
                <Pill color={source === "backend" ? "green" : "gold"}>{source === "backend" ? "Live (synced)" : source === "local" ? "Local draft" : "Default"}</Pill>
                <a href={`/legal/${slug}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--neon-cyan)] flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Preview</a>
              </div>
              <label className="mt-3 block text-xs uppercase tracking-widest text-[var(--text-muted)]">Title</label>
              <input value={doc.title} onChange={(e) => setDoc((s) => ({ ...s, title: e.target.value }))} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="content-title" />
              <label className="mt-3 block text-xs uppercase tracking-widest text-[var(--text-muted)]">Body</label>
              <p className="text-[11px] text-[var(--text-muted)]">Use "## Heading" for section headings and "- " for bullet points.</p>
              <textarea value={doc.body} onChange={(e) => setDoc((s) => ({ ...s, body: e.target.value }))} rows={18} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono" data-testid="content-body" />
              <div className="mt-3 flex gap-2">
                <NeonButton variant="pink" onClick={save} loading={saving} data-testid="content-save"><Save className="h-4 w-4" /> Save</NeonButton>
                <NeonButton variant="ghost" onClick={reset}><RotateCcw className="h-4 w-4" /> Reset to default</NeonButton>
              </div>
            </>
          )}
        </NeonCard>
      </div>
    </div>
  );
}
